export type Period = 'today' | 'yesterday' | 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'lastYear' | 'last3months' | 'last12months' | 'custom'
export type SortParams = 'newest' | 'oldest' | 'nameAsc' | 'nameDesc' | 'priceAsc' | 'priceDesc'
export type FilterStatus = 'published' | 'draft' | 'active' | 'inactive' | 'suspended' | 'pending' | string

export const getDateRange = (
  period: Period,
  custom?: { start?: string; end?: string },
) => {
  // Custom range: use the user-supplied YYYY-MM-DD bounds; send whichever side
  // is provided so a one-sided range (e.g. "from X onwards") still works.
  if (period === 'custom') {
    const out: Record<string, string> = {};
    if (custom?.start) out.start_ts = custom.start;
    if (custom?.end) out.end_ts = custom.end;
    return out;
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let start = new Date();
  let end = new Date();

  switch (period) {
    case 'today':
      start = today;
      end = new Date(today.getTime() + 86400000 - 1);
      break;
    case 'yesterday':
      start = new Date(today.getTime() - 86400000);
      end = new Date(today.getTime() - 1);
      break;
    case 'last7days':
      start = new Date(today.getTime() - 7 * 86400000);
      end = new Date();
      break;
    case 'last30days':
      start = new Date(today.getTime() - 30 * 86400000);
      end = new Date();
      break;
    case 'last3months':
      start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      end = new Date();
      break;
    case 'last12months':
      start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      end = new Date();
      break;
    case 'thisMonth':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      break;
    case 'lastMonth':
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      break;
    case 'thisYear':
      start = new Date(now.getFullYear(), 0, 1);
      end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
      break;
    case 'lastYear':
      start = new Date(now.getFullYear() - 1, 0, 1);
      end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
      break;
    default:
      return {};
  }

  return {
    start_ts: start.toISOString().split('T')[0],
    end_ts: end.toISOString().split('T')[0]
  };
};

export const getSortParams = (sort: SortParams) => {
  switch (sort) {
    case 'newest':
      return { sort: 'date_added', order: 'desc' };
    case 'oldest':
      return { sort: 'date_added', order: 'asc' };
    case 'nameAsc':
      return { sort: 'name', order: 'asc' };
    case 'nameDesc':
      return { sort: 'name', order: 'desc' };
    case 'priceAsc':
      return { sort: 'price', order: 'asc' };
    case 'priceDesc':
      return { sort: 'price', order: 'desc' };
    default:
      return { sort: 'date_added', order: 'desc' };
  }
};

export const getListingStatusFilter = (status: FilterStatus) => {
    switch (status) {
      case 'published':
        return { published: 1 };
      case 'draft':
        return { published: 0 };
      case 'active':
        return { active: 1 };
      case 'inactive':
        return { active: 0 };
      default:
        return {};
    }
  };

export const getOrganizationStatusFilter = (status: string) => {
  switch (status) {
    case 'active':
      return { active: 1 };
    case 'inactive':
      return { active: 0 };
    case 'suspended':
      return { suspended: 1 };
    case 'pending':
      return { pending: 1 };
    default:
      return {};
  }
};

export const getUserStatusFilter = (status: string) => {
  switch (status) {
    case 'Active':
      return { active: 1 };
    case 'Inactive':
      return { active: 0 };
    default:
      return {};
  }
};

  type Status =
  | 'active'
  | 'paused'
  | 'scheduled'
  | 'draft'
  | 'expired'
  | 'canceled'
  | 'inactive';

type BackendStatusFilter = Partial<{
  active: 0 | 1;
  schedule_optim: 0 | 1;
  published: 0 | 1;
  expired: 0 | 1;
  canceled: 0 | 1;
}>;


const STATUS_FILTER_MAP: Record<Status, BackendStatusFilter> = {
  active: {
    active: 1,
  },
  paused: {
    active: 0,
  },
  scheduled: {
    schedule_optim: 1,
  },
  draft: {
    published: 0,
  },
  expired: {
    expired: 1,
  },
  canceled: {
    canceled: 1,
  },
  inactive: {
    active: 0,
  },
};

export function getPromotionStatusFilter(
  status?: Status
): BackendStatusFilter {
  if (!status) return {};

  return STATUS_FILTER_MAP[status] ?? {};
}

export const getUserRoleFilter = (role: string) => {
  switch (role) {
    case 'Admin':
      return { admin: 1 };
    case 'User':
      return { admin: 0 }; 
    default:
      return {};
  }
};

export const getUserVerificationFilter = (verification: string) => {
  switch (verification) {
    case 'verified':
      return { email_verified: 1 };
    case 'unverified':
      return { email_verified: 0 };
    default:
      return {};
  }
};

export const getOrganizationSizeFilter = (size: string) => {
  switch (size) {
    case 'small':
      return { size: '10' };
    case 'medium':
      return { size: '11-50' };
    case 'large':
      return { size: '51-200' };
    case 'enterprise':
      return { size: '201' };
    default:
      return {};
  }
};

export const getOrganizationListingsFilter = (listings: string) => {
  switch (listings) {
    case '0-10':
      return { listings: '10' };
    case '11-50':
      return { listings: '11-50' };
    case '51-200':
      return { listings: '51-200' };
    case '201+':
      return { listings: '201' };
    default:
      return {};
  }
};

export type EntityStatusLabel =
  | 'DELETED' | 'ARCHIVED' | 'REJECTED' | 'REVIEW'
  | 'LIVE' | 'INACTIVE' | 'DRAFT';

const truthyFlag = (v: unknown) => v === 1 || v === true || v === '1';

export const deriveEntityStatus = (entity: any): EntityStatusLabel => {
  const e = entity?.Entity ?? entity ?? {};
  const server = (e.status ?? entity?.status) as string | undefined;
  if (server && typeof server === 'string') {
    const up = server.toUpperCase();
    if (up) return up as EntityStatusLabel;
  }
  if (truthyFlag(e.deleted)) return 'DELETED';
  if (truthyFlag(e.archive_perm) || truthyFlag(e.archive)) return 'ARCHIVED';
  if (truthyFlag(e.rejected)) return 'REJECTED';
  if (truthyFlag(e.review)) return 'REVIEW';
  if (truthyFlag(e.published) && truthyFlag(e.active)) return 'LIVE';
  if (truthyFlag(e.published) && !truthyFlag(e.active)) return 'INACTIVE';
  return 'DRAFT';
};

/** Tailwind badge classes for a canonical entity status. */
export const entityStatusBadgeClass = (status: string): string => {
  switch (status.toUpperCase()) {
    case 'LIVE': return 'bg-green-100 text-green-800';
    case 'REVIEW': return 'bg-amber-100 text-amber-800';
    case 'REJECTED': return 'bg-rose-100 text-rose-800';
    case 'SUSPENDED': return 'bg-orange-100 text-orange-800';
    case 'INACTIVE': return 'bg-gray-100 text-gray-700';
    case 'ARCHIVED': return 'bg-gray-100 text-gray-700';
    case 'DELETED': return 'bg-red-100 text-red-800';
    case 'DRAFT':
    default: return 'bg-slate-100 text-slate-700';
  }
};
